import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';

const LOCALE_MODULES: Record<string, () => Promise<{ default: Record<string, any> }>> = import.meta.glob('./locales/**/*.json', { eager: false }) as Record<string, () => Promise<{ default: Record<string, any> }>>;

const EXCLUDED_LOCALE_CODES = new Set(['en-backup2', 'en-broken', 'en_backup2', 'en_broken']);

const LANGUAGE_METADATA: Record<string, { name: string; nativeName: string }> = {
  ar: { name: 'Arabic', nativeName: 'العربية' },
  bg: { name: 'Bulgarian', nativeName: 'Български' },
  cs: { name: 'Czech', nativeName: 'Čeština' },
  da: { name: 'Danish', nativeName: 'Dansk' },
  de: { name: 'German', nativeName: 'Deutsch' },
  el: { name: 'Greek', nativeName: 'Ελληνικά' },
  en: { name: 'English', nativeName: 'English' },
  es: { name: 'Spanish', nativeName: 'Español' },
  fi: { name: 'Finnish', nativeName: 'Suomi' },
  fr: { name: 'French', nativeName: 'Français' },
  he: { name: 'Hebrew', nativeName: 'עברית' },
  hr: { name: 'Croatian', nativeName: 'Hrvatski' },
  hu: { name: 'Hungarian', nativeName: 'Magyar' },
  it: { name: 'Italian', nativeName: 'Italiano' },
  ja: { name: 'Japanese', nativeName: '日本語' },
  nl: { name: 'Dutch', nativeName: 'Nederlands' },
  no: { name: 'Norwegian', nativeName: 'Norsk' },
  pl: { name: 'Polish', nativeName: 'Polski' },
  pt: { name: 'Portuguese', nativeName: 'Português' },
  ro: { name: 'Romanian', nativeName: 'Română' },
  ru: { name: 'Russian', nativeName: 'Русский' },
  sk: { name: 'Slovak', nativeName: 'Slovenčina' },
  sl: { name: 'Slovenian', nativeName: 'Slovenščina' },
  sr: { name: 'Serbian', nativeName: 'Српски' },
  sv: { name: 'Swedish', nativeName: 'Svenska' },
  tr: { name: 'Turkish', nativeName: 'Türkçe' },
  uk: { name: 'Ukrainian', nativeName: 'Українська' },
  zh: { name: 'Chinese', nativeName: '中文' },
};

const localeFilePattern = /^[a-z]{2,3}(?:-[a-z0-9]+)*$/i;

const extractLocaleCode = (filePath: string): string => {
  const normalizedPath = filePath.replace(/\\/g, '/');
  const afterLocales = normalizedPath.split('/locales/')[1] || '';
  const segments = afterLocales.split('/').filter(Boolean);

  if (segments.length === 0) return '';
  if (segments.length > 1) {
    return segments[0].replace(/_/g, '-').toLowerCase();
  }

  const fileName = segments[0];
  return fileName.replace(/\.json$/i, '').replace(/_/g, '-').toLowerCase();
};

export function normalizeLanguageCode(lang?: string): string {
  const normalized = (lang || 'en').toString().replace(/_/g, '-').toLowerCase().trim();
  if (!normalized) return 'en';
  return normalized.split('-')[0];
}

const discoveredLocaleCodes = Object.keys(LOCALE_MODULES)
  .map(extractLocaleCode)
  .filter((code) => localeFilePattern.test(code) && !EXCLUDED_LOCALE_CODES.has(code))
  .map((code) => code.toLowerCase());

const uniqueLocaleCodes = Array.from(new Set(discoveredLocaleCodes));

export const SUPPORTED_LANGUAGES = uniqueLocaleCodes
  .map((code) => {
    const known = LANGUAGE_METADATA[code];
    if (known) {
      return { code, name: known.name, nativeName: known.nativeName };
    }

    let name = code.toUpperCase();
    let nativeName = code.toUpperCase();
    try {
      const englishNames = new Intl.DisplayNames(['en'], { type: 'language' });
      const localizedNames = new Intl.DisplayNames([code], { type: 'language' });
      name = englishNames.of(code) || name;
      nativeName = localizedNames.of(code) || name;
    } catch {
      // Ignore and keep fallback labels for unknown locale codes.
    }

    return { code, name, nativeName };
  })
  .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

export const SUPPORTED_LANGUAGE_CODES = new Set(SUPPORTED_LANGUAGES.map((language) => language.code));

export function isSupportedLanguage(lang?: string): boolean {
  return SUPPORTED_LANGUAGE_CODES.has(normalizeLanguageCode(lang));
}

const resources = {
  en: { translation: en },
};

type LocaleLoader = () => Promise<{ default: Record<string, any> }>;

const localeLoaders: Record<string, LocaleLoader> = {};
for (const [filePath, loader] of Object.entries(LOCALE_MODULES)) {
  const code = extractLocaleCode(filePath);
  if (localeFilePattern.test(code) && !EXCLUDED_LOCALE_CODES.has(code) && code !== 'en') {
    localeLoaders[code] = loader as LocaleLoader;
  }
}

const loadedLanguages = new Set<string>(['en']);

async function ensureLanguageLoaded(language: string): Promise<void> {
  const normalized = (language || 'en').toString().replace(/_/g, '-').toLowerCase();
  if (loadedLanguages.has(normalized)) return;

  const exactLoader = localeLoaders[normalized];
  const baseCode = normalizeLanguageCode(normalized);
  const loader = exactLoader || localeLoaders[baseCode];
  if (!loader) return;

  try {
    const module = await loader();
    const resourceCode = exactLoader ? normalized : baseCode;
    i18n.addResourceBundle(resourceCode, 'translation', module.default, true, true);
    loadedLanguages.add(resourceCode);
  } catch (error) {
    console.warn(`Failed to load language resources for ${normalized}:`, error);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: Array.from(SUPPORTED_LANGUAGE_CODES),
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    cleanCode: true,
    defaultNS: 'translation',
    fallbackNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    partialBundledLanguages: true,
    react: {
      useSuspense: false,
    },
  });

const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (async (lng?: string, ...args: any[]) => {
  const normalizedLng = lng ? normalizeLanguageCode(lng) : lng;

  if (normalizedLng) {
    await ensureLanguageLoaded(normalizedLng);
  }

  return originalChangeLanguage(normalizedLng, ...args);
}) as typeof i18n.changeLanguage;

const preferredLanguage = (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null) ||
  (typeof navigator !== 'undefined' ? navigator.language : 'en');

void ensureLanguageLoaded(preferredLanguage || 'en').then(() => {
  const preferredRaw = (preferredLanguage || 'en').toString();
  const preferredSanitized = preferredRaw.startsWith('"') && preferredRaw.endsWith('"')
    ? preferredRaw.slice(1, -1)
    : preferredRaw;
  const normalized = normalizeLanguageCode(preferredSanitized);
  if (normalized && normalized !== i18n.language) {
    void i18n.changeLanguage(normalized);
  }
});

i18n.on('languageChanged', (lng) => {
  const normalized = normalizeLanguageCode(lng);
  document.documentElement.lang = normalized;
  document.documentElement.dir = i18n.dir(normalized);
  localStorage.setItem('i18nextLng', normalized);

  void ensureLanguageLoaded(normalized);
});

export default i18n;
